import React, { Component, createRef } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { hideModal } from "../store/navSlice";
import pkg from "../../package.json";

import "./Modal.scss";
import { ReactComponent as IconPlay } from "../assets/images/play.svg";
import { ReactComponent as IconPause } from "../assets/images/pause.svg";
import { ReactComponent as IconClose } from "../assets/images/close.svg";

function clamp(n, min, max) {
  return Math.max(Math.min(n, max), min);
}

const mapDispatch = { hideModal };
function mapState(state) {
  return { modal: state.nav.modal };
}

class Modal extends Component {
  constructor(props) {
    super();

    this.basepath = (pkg.homepage || "") + "/content";

    this.state = {
      modalDelayed: props.modal,
      duration: 0,
      elapsed: 0,
      progress: 0,
      isPlaying: false,
      wasPlaying: false,
      isDragging: false,
      dragMin: null,
      dragMax: null,
      dragPos: null,
    };

    this.modal = createRef();
    this.video = createRef();
    this.videoEl = null;
    this.scrubberBar = createRef();
  }

  componentDidMount() {
    if (!this.videoEl && this.video.current) {
      this.videoEl = this.video.current;
      this.videoEl.addEventListener(
        "durationchange",
        this.onDurationChange.bind(this)
      );
      this.videoEl.addEventListener("timeupdate", this.onTimeUpdate.bind(this));
      this.videoEl.addEventListener("play", this.onPlay.bind(this));
      this.videoEl.addEventListener("pause", this.onPauseOrEnd.bind(this));
    }
  }

  componentDidUpdate(prevProps) {
    const modal = this.props.modal;
    const prevModal = prevProps.modal;

    // Delay unsetting the modal so out transition can play
    if (prevModal !== null && modal === null) {
      clearInterval(this.timerID);
      this.timerID = setTimeout(() => {
        this.setState({ modalDelayed: modal });
      }, 500);
    } else if (prevModal !== modal) {
      this.setState({ modalDelayed: modal });
    }
  }

  onDurationChange() {
    this.setState({
      duration: this.videoEl.duration,
    });
  }

  onTimeUpdate() {
    this.setState({
      elapsed: this.videoEl.currentTime,
    });
  }

  onPlay() {
    this.setState({ isPlaying: true });
  }

  onPauseOrEnd() {
    this.setState({ isPlaying: false });
  }

  onEnded() {
    console.log("ended");
    this.setState({ isPlaying: false });
  }

  togglePlay() {
    if (this.state.isPlaying) {
      this.videoEl.pause();
    } else {
      this.videoEl.play();
    }
  }

  jumpTo(event) {
    if (this.scrubberBar.current === null) {
      return;
    }

    const rect = this.scrubberBar.current.getBoundingClientRect();
    const offset = rect.left;
    const end = rect.width;
    const x = event.clientX - offset;
    const jumpToPercent = (x * 100) / end;
    const jumpTo = (jumpToPercent * this.state.duration) / 100;

    this.videoEl.currentTime = jumpTo;
  }

  onDragStart(event) {
    const modal = this.modal.current;
    const scrubberBar = this.scrubberBar.current;

    if (modal === null || scrubberBar === null) {
      return;
    }

    const pageX = event.touches ? event.touches[0].pageX : event.pageX;

    const rect = scrubberBar.getBoundingClientRect();
    const dragMin = rect.left;
    const dragMax = dragMin + rect.width;
    const dragPos = clamp(pageX, dragMin, dragMax);
    const dragPosPercent = (dragPos - dragMin) * 100 / (dragMax - dragMin);

    this.setState({
      isDragging: true,
      wasPlaying: this.state.isPlaying,
      dragMin,
      dragMax,
      dragPos: dragPosPercent
    });

    this.videoEl.pause();

    modal.addEventListener("touchmove", this.onDrag);
    modal.addEventListener("mousemove", this.onDrag);
    modal.addEventListener("touchend", this.onDragEnd);
    modal.addEventListener("mouseup", this.onDragEnd);
  }

  onDrag = (event) => {
    const { dragMin, dragMax } = this.state;
    const pageX = event.touches ? event.touches[0].pageX : event.pageX;
    const dragPos = clamp(pageX, dragMin, dragMax);
    const dragPosPercent = (dragPos - dragMin) * 100 / (dragMax - dragMin);

    const jumpTo = (dragPosPercent * this.state.duration) / 100;
    this.videoEl.currentTime = jumpTo;

    this.setState({ dragPos: dragPosPercent });
  }

  onDragEnd = (e) => {
    const modal = this.modal.current;    

    if (modal === null) {
      return;
    }
    
    if (this.state.wasPlaying) {
      this.videoEl.play();
    }

    // Delay unsetting the flag to handle issue were ending drag outside of
    // player triggers modal backfrop click closing modal
    setTimeout(() => {
      this.setState({ isDragging: false });      
    }, 500);

    modal.removeEventListener("touchmove", this.onDrag);
    modal.removeEventListener("mousemove", this.onDrag);
    modal.removeEventListener("touchend", this.onDragEnd);
    modal.removeEventListener("mouseup", this.onDragEnd);
  }

  _secondsToMinutesAndSeconds(seconds) {
    let mins = Math.floor(seconds / 60);
    let secs = Math.floor(seconds - mins * 60);

    mins = mins < 10 ? "0" + mins : mins;
    secs = secs < 10 ? "0" + secs : secs;

    return `${mins}:${secs}`;
  }

  render() {
    const { t, modal, hideModal } = this.props;
    const { modalDelayed, duration, elapsed, isPlaying } = this.state;
    const { isDragging, dragPos } = this.state;
    const progress = (elapsed * 100) / duration + "%";
    const thumbPos = isDragging ? dragPos + "%" : progress;
    const transKey = modalDelayed?.transKey || false;

    let title = "";
    if (transKey) {
      title = t(`hotspots.${transKey}_title`);
    }

    return (
      <div
        ref={this.modal}
        className={`app-modal ${modal ? "show" : ""}`}
        onClick={() => !isDragging && hideModal()}
      >
        <div
          className="modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="head">{title}</div>
          <video
            ref={this.video}
            className="player"
            autoPlay
            src={`${this.basepath}/${modalDelayed?.src}`}
            onClick={() => this.togglePlay()}
          />
          <div className="scrubber">
            <div
              ref={this.scrubberBar}
              className="bar"
              onClick={(e) => this.jumpTo(e)}
            ></div>
            <div className="progress-wrapper">
              <div className="progress" style={{ width: progress }}></div>
            </div>
            <div className="thumb-wrapper">
              <div
                onMouseDown={this.onDragStart.bind(this)}
                onTouchStart={this.onDragStart.bind(this)}
                className={`thumb ${isDragging ? 'dragging' : ''}`}
                style={{ left: thumbPos }}
              ></div>
            </div>
          </div>
          <div className="body">
            <div className="info noselect">
              {this._secondsToMinutesAndSeconds(elapsed)}
            </div>
            <button
              className="btn btn-circle"
              onClick={() => this.togglePlay()}
            >
              {isPlaying ? <IconPause /> : <IconPlay />}
            </button>
            <div className="info noselect duration">
              {this._secondsToMinutesAndSeconds(duration)}
            </div>
          </div>
          <button
            className="btn btn-circle modal-close"
            onClick={() => hideModal()}
          >
            <IconClose />
          </button>
        </div>
      </div>
    );
  }
}

export default withTranslation()(connect(mapState, mapDispatch)(Modal));
